import type { OnSearchData } from '@/app/(routes)/listing/types';
import {
  FILTER_TYPE_NAME_CATEGORY,
  FILTER_TYPE_NAME_COE_TYPE,
  FILTER_TYPE_NAME_COUNTRY,
  FILTER_TYPE_NAME_DEALER_TYPE,
  FILTER_TYPE_NAME_DEPRECIATION,
  FILTER_TYPE_NAME_DRIVE_RANGE,
  FILTER_TYPE_NAME_ENERGY_CONSUPTION,
  FILTER_TYPE_NAME_ENGINE_CAP,
  FILTER_TYPE_NAME_FUEL_CONSUMPTION,
  FILTER_TYPE_NAME_FUEL_TYPE,
  FILTER_TYPE_NAME_OMV,
  FILTER_TYPE_NAME_POWER_OUTPUT,
  FILTER_TYPE_NAME_PRICE_RANGE,
  FILTER_TYPE_NAME_TRANSIMISSION,
  VEHICLE_NAME_ALL_COMMERCIAL_VEHICLES,
  VEHICLE_NAME_ALL_PASSENGER_CARS,
  VEHICLE_NAME_ELECTRIC_CARS,
  VEHICLE_NAME_HATCHBACK,
  VEHICLE_NAME_HYBRID_CARS,
  VEHICLE_NAME_LUXURY_SEDAN,
  VEHICLE_NAME_MID_SIZED_SEDAN,
  VEHICLE_NAME_MPV,
  VEHICLE_NAME_SPORTS_CARS,
  VEHICLE_NAME_STATIONWAGON,
  VEHICLE_NAME_SUV,
  VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES,
  VEHICLE_VALUE_ALL_PASSENGER_CARS,
  VEHICLE_VALUE_ELECTRIC_CARS,
  VEHICLE_VALUE_HATCHBACK,
  VEHICLE_VALUE_HYBRID_CARS,
  VEHICLE_VALUE_LUXURY_SEDAN,
  VEHICLE_VALUE_MID_SIZED_SEDAN,
  VEHICLE_VALUE_MPV,
  VEHICLE_VALUE_SPORTS_CARS,
  VEHICLE_VALUE_STATIONWAGON,
  VEHICLE_VALUE_SUV,
} from '@/constants/global';
import {
  categoryToText,
  coeTypeToText,
  conditionToText,
  dealerTypeToText,
  fuelTypeToText,
  transmissionToText,
} from '@/hooks/use-filter-params/configs';

import {
  ENGINE_CAP_SELECT_OPTION_VALUE_661_TO_1600,
  ENGINE_CAP_SELECT_OPTION_VALUE_1601_TO_2000,
  ENGINE_CAP_SELECT_OPTION_VALUE_2001_TO_3000,
  ENGINE_CAP_SELECT_OPTION_VALUE_LESS_THAN_661,
  ENGINE_CAP_SELECT_OPTION_VALUE_MORE_THAN_3000,
} from '../constants';
import type {
  FilterChoice,
  FilterRange,
  FilterSelect,
} from '../filter-label-list/types';
import { CheckboxGroup } from './input/checkbox-group';
import type { CheckboxValue } from './input/checkbox-group/types';
import { Choice } from './input/choice';
import { ConditionSelect } from './input/condition-select';
import { Range } from './input/range';
import { Select } from './input/select';
import type { Category } from './types';

export const inputComponents = {
  choice: Choice,
  select: Select,
  range: Range,
  checkbox: CheckboxGroup,
  conditionSelect: ConditionSelect,
};

export const convertChoiceCondition = (
  lower: number | undefined,
  upper: number | undefined,
) => {
  if (lower && upper) {
    return {
      text: conditionToText['='],
      value: '=',
    };
  }

  if (lower) {
    return {
      text: conditionToText['>'],
      value: '>',
    };
  }

  if (upper) {
    return {
      text: conditionToText['<'],
      value: '<',
    };
  }
  return {
    text: '',
    value: '',
  };
};

export const defaultCheckboxValue: (
  defaultSearchData: OnSearchData
) => Record<string, Category<'checkbox'>
& { values: Record<string, CheckboxValue>; }> = (data) => {
  const vehicleTypes = data.query.vehicleType?.split(',') ?? [];

  return {
    'Vehicle Type': {
      name: 'Vehicle Type',
      type: 'checkbox',
      values: {
        [VEHICLE_NAME_ALL_COMMERCIAL_VEHICLES]: {
          name: VEHICLE_NAME_ALL_COMMERCIAL_VEHICLES,
          value: VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES,
          checked: vehicleTypes.includes(VEHICLE_VALUE_ALL_COMMERCIAL_VEHICLES),
        },
        [VEHICLE_NAME_ALL_PASSENGER_CARS]: {
          name: VEHICLE_NAME_ALL_PASSENGER_CARS,
          value: VEHICLE_VALUE_ALL_PASSENGER_CARS,
          checked: vehicleTypes.includes(VEHICLE_VALUE_ALL_PASSENGER_CARS),
        },
        [VEHICLE_NAME_MID_SIZED_SEDAN]: {
          name: VEHICLE_NAME_MID_SIZED_SEDAN,
          value: VEHICLE_VALUE_MID_SIZED_SEDAN,
          checked: vehicleTypes.includes(VEHICLE_VALUE_MID_SIZED_SEDAN),
        },
        [VEHICLE_NAME_LUXURY_SEDAN]: {
          name: VEHICLE_NAME_LUXURY_SEDAN,
          value: VEHICLE_VALUE_LUXURY_SEDAN,
          checked: vehicleTypes.includes(VEHICLE_VALUE_LUXURY_SEDAN),
        },
        [VEHICLE_NAME_HATCHBACK]: {
          name: VEHICLE_NAME_HATCHBACK,
          value: VEHICLE_VALUE_HATCHBACK,
          checked: vehicleTypes.includes(VEHICLE_VALUE_HATCHBACK),
        },
        [VEHICLE_NAME_MPV]: {
          name: VEHICLE_NAME_MPV,
          value: VEHICLE_VALUE_MPV,
          checked: vehicleTypes.includes(VEHICLE_VALUE_MPV),
        },
        [VEHICLE_NAME_SUV]: {
          name: VEHICLE_NAME_SUV,
          value: VEHICLE_VALUE_SUV,
          checked: vehicleTypes.includes(VEHICLE_VALUE_SUV),
        },
        [VEHICLE_NAME_SPORTS_CARS]: {
          name: VEHICLE_NAME_SPORTS_CARS,
          value: VEHICLE_VALUE_SPORTS_CARS,
          checked: vehicleTypes.includes(VEHICLE_VALUE_SPORTS_CARS),
        },
        [VEHICLE_NAME_STATIONWAGON]: {
          name: VEHICLE_NAME_STATIONWAGON,
          value: VEHICLE_VALUE_STATIONWAGON,
          checked: vehicleTypes.includes(VEHICLE_VALUE_STATIONWAGON),
        },
        [VEHICLE_NAME_HYBRID_CARS]: {
          name: VEHICLE_NAME_HYBRID_CARS,
          value: VEHICLE_VALUE_HYBRID_CARS,
          checked: vehicleTypes.includes(VEHICLE_VALUE_HYBRID_CARS),
        },
        [VEHICLE_NAME_ELECTRIC_CARS]: {
          name: VEHICLE_NAME_ELECTRIC_CARS,
          value: VEHICLE_VALUE_ELECTRIC_CARS,
          checked: vehicleTypes.includes(VEHICLE_VALUE_ELECTRIC_CARS),
        },
      },
    },
  };
};

export const defaultChoiceValue: (
  defaultSearchData: OnSearchData
) => Record<string, FilterChoice> = data => ({
  [FILTER_TYPE_NAME_OMV]: {
    name: FILTER_TYPE_NAME_OMV,
    type: 'choice',
    optionValue: {
      text: convertChoiceCondition(data.query.minOmv, data.query.maxOmv).text,
      value: convertChoiceCondition(data.query.minOmv, data.query.maxOmv).value,
    },
    unit: {
      type: 'prefix',
      value: '$',
    },
    inputValue: (data.query.minOmv && data.query.maxOmv)
      ? data.query.minOmv.toString(10)
      : (data.query.minOmv || data.query.maxOmv)?.toString(10) ?? '',
  },
  [FILTER_TYPE_NAME_ENERGY_CONSUPTION]: {
    name: FILTER_TYPE_NAME_ENERGY_CONSUPTION,
    type: 'choice',
    optionValue: {
      text: convertChoiceCondition(
        data.query.minEnergyConsumption,
        data.query.maxEnergyConsumption,
      ).text,
      value: convertChoiceCondition(
        data.query.minEnergyConsumption,
        data.query.maxEnergyConsumption,
      ).value,
    },
    unit: {
      type: 'suffix',
      value: 'kWh',
    },
    inputValue: (data.query.minEnergyConsumption
    && data.query.maxEnergyConsumption)
      ? data.query.minEnergyConsumption.toString(10)
      : (data.query.minEnergyConsumption
      || data.query.maxEnergyConsumption)?.toString(10)
      ?? '',
  },
  [FILTER_TYPE_NAME_POWER_OUTPUT]: {
    name: FILTER_TYPE_NAME_POWER_OUTPUT,
    type: 'choice',
    optionValue: {
      text: convertChoiceCondition(
        data.query.minPowerOutput,
        data.query.maxPowerOutput,
      ).text,
      value: convertChoiceCondition(
        data.query.minPowerOutput,
        data.query.maxPowerOutput,
      ).value,
    },
    unit: {
      type: 'suffix',
      value: 'bhp',
    },
    inputValue: (data.query.minPowerOutput && data.query.maxPowerOutput)
      ? data.query.minPowerOutput.toString(10)
      : (data.query.minPowerOutput || data.query.maxPowerOutput)?.toString(10)
      ?? '',
  },
  [FILTER_TYPE_NAME_FUEL_CONSUMPTION]: {
    name: FILTER_TYPE_NAME_FUEL_CONSUMPTION,
    type: 'choice',
    optionValue: {
      text: convertChoiceCondition(
        data.query.minFuelEconomy,
        data.query.maxFuelEconomy,
      ).text,
      value: convertChoiceCondition(
        data.query.minFuelEconomy,
        data.query.maxFuelEconomy,
      ).value,
    },
    unit: {
      type: 'suffix',
      value: 'km/L',
    },
    inputValue: (data.query.minFuelEconomy && data.query.maxFuelEconomy)
      ? data.query.minFuelEconomy.toString(10)
      : (data.query.minFuelEconomy || data.query.maxFuelEconomy)?.toString(10)
      ?? '',
  },
});

export const defaultRangeValue: (
  defaultSearchData: OnSearchData
) => Record<string, FilterRange> = data => ({
  [FILTER_TYPE_NAME_PRICE_RANGE]: {
    name: FILTER_TYPE_NAME_PRICE_RANGE,
    type: 'range',
    lowerValue: {
      text: data.query.minPrice
        ? `$${(+data.query.minPrice).toLocaleString('en-US')}`
        : 'Lowest',
      value: data.query.minPrice ?? '',
    },
    upperValue: {
      text: data.query.maxPrice
        ? `$${(+data.query.maxPrice).toLocaleString('en-US')}`
        : 'Highest',
      value: data.query.maxPrice ?? '',
    },
  },
  [FILTER_TYPE_NAME_DEPRECIATION]: {
    name: FILTER_TYPE_NAME_DEPRECIATION,
    type: 'range',
    lowerValue: {
      text: data.query.minDepreciation
        ? `$${(+data.query.minDepreciation).toLocaleString('en-US')}/yr`
        : 'Lowest',
      value: data.query.minDepreciation ?? '',
    },
    upperValue: {
      text: data.query.maxDepreciation
        ? `$${(+data.query.maxDepreciation).toLocaleString('en-US')}/yr`
        : 'Highest',
      value: data.query.maxDepreciation ?? '',
    },
  },
  [FILTER_TYPE_NAME_DRIVE_RANGE]: {
    name: FILTER_TYPE_NAME_DRIVE_RANGE,
    type: 'range',
    lowerValue: {
      text: data.query.minDriveRange
        ? `$${(+data.query.minDriveRange).toLocaleString('en-US')} km`
        : 'Lowest',
      value: data.query.minDriveRange ?? '',
    },
    upperValue: {
      text: data.query.maxDriveRange
        ? `${(+data.query.maxDriveRange).toLocaleString('en-US')} km`
        : 'Highest',
      value: data.query.maxDriveRange ?? '',
    },
  },
});

export const defaultSelectValue: (
  defaultSearchData: OnSearchData
) => Record<string, FilterSelect> = data => ({
  [FILTER_TYPE_NAME_CATEGORY]: {
    name: FILTER_TYPE_NAME_CATEGORY,
    type: 'select',
    optionValue: {
      text: data.query.category
        ? categoryToText[data.query.category]
        : '',
      value: data.query.category ?? '',
    },
  },
  [FILTER_TYPE_NAME_TRANSIMISSION]: {
    name: FILTER_TYPE_NAME_TRANSIMISSION,
    type: 'select',
    optionValue: {
      text: data.query.transmission
        ? transmissionToText[data.query.transmission]
        : '',
      value: data.query.transmission ?? '',
    },
  },
  [FILTER_TYPE_NAME_ENGINE_CAP]: {
    name: FILTER_TYPE_NAME_ENGINE_CAP,
    type: 'select',
    optionValue: (() => {
      if (data.query.minEngineCapacity === undefined
        && data.query.maxEngineCapacity === 661) {
        return {
          text: '< 661cc',
          value: ENGINE_CAP_SELECT_OPTION_VALUE_LESS_THAN_661,
        };
      }

      if (data.query.minEngineCapacity === 661
        && data.query.maxEngineCapacity === 1600) {
        return {
          text: '661cc - 1,600cc',
          value: ENGINE_CAP_SELECT_OPTION_VALUE_661_TO_1600,
        };
      }

      if (data.query.minEngineCapacity === 1601
        && data.query.maxEngineCapacity === 2000) {
        return {
          text: '1,601cc - 2,000cc',
          value: ENGINE_CAP_SELECT_OPTION_VALUE_1601_TO_2000,
        };
      }

      if (data.query.minEngineCapacity === 2001
        && data.query.maxEngineCapacity === 3000) {
        return {
          text: '2,001cc - 3,000cc',
          value: ENGINE_CAP_SELECT_OPTION_VALUE_2001_TO_3000,
        };
      }

      if (data.query.minEngineCapacity === 3000
        && data.query.maxEngineCapacity === undefined) {
        return {
          text: '> 3,000cc',
          value: ENGINE_CAP_SELECT_OPTION_VALUE_MORE_THAN_3000,
        };
      }

      return {
        text: '',
        value: '',
      };
    })(),
  },
  [FILTER_TYPE_NAME_FUEL_TYPE]: {
    name: FILTER_TYPE_NAME_FUEL_TYPE,
    type: 'select',
    optionValue: {
      text: data.query.fuelType
        ? fuelTypeToText[data.query.fuelType]
        : '',
      value: data.query.fuelType ?? '',
    },
  },
  [FILTER_TYPE_NAME_DEALER_TYPE]: {
    name: FILTER_TYPE_NAME_DEALER_TYPE,
    type: 'select',
    optionValue: {
      text: data.query.dealerType
        ? dealerTypeToText[data.query.dealerType]
        : 'Any',
      value: data.query.dealerType ?? '',
    },
  },
  [FILTER_TYPE_NAME_COE_TYPE]: {
    name: FILTER_TYPE_NAME_COE_TYPE,
    type: 'select',
    optionValue: {
      text: data.query.coeCategory
        ? coeTypeToText[data.query.coeCategory]
        : 'Any',
      value: data.query.coeCategory ?? '',
    },
  },
  [FILTER_TYPE_NAME_COUNTRY]: {
    name: FILTER_TYPE_NAME_COUNTRY,
    type: 'select',
    optionValue: {
      text: data.query.country ?? '',
      value: data.query.country ?? '',
    },
  },
});
